import React, { useState, useRef, useEffect } from 'react';
import { User, Settings, LogOut, CreditCard, HelpCircle } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import Avatar from '../Avatar/Avatar';

/**
 * Menu déroulant du profil utilisateur avec avatar
 */
export default function UserMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Fermer le menu si on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Générer les initiales à partir du nom de l'utilisateur
  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const userName = user?.name || user?.username || 'Utilisateur';
  const userEmail = user?.email || '';
  const userInitials = getInitials(userName);
  const userAvatar = user?.avatar || user?.profilePicture;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const menuItems = [
    {
      icon: User,
      label: 'Mon Profil',
      action: () => {
        setIsOpen(false);
        // Navigate to profile page
      }
    },
    {
      icon: Settings,
      label: 'Paramètres',
      action: () => {
        setIsOpen(false);
        navigate('/settings');
      }
    },
    {
      icon: CreditCard,
      label: 'Abonnement',
      action: () => {
        setIsOpen(false);
        navigate('/settings?tab=subscription');
      }
    },
    {
      icon: HelpCircle,
      label: 'Aide & Support',
      action: () => {
        setIsOpen(false);
        // Navigate to help page
      }
    },
    {
      icon: LogOut,
      label: 'Déconnexion',
      action: handleLogout,
      danger: true
    }
  ];

  return (
    <div className="relative" ref={menuRef}>
      {/* Avatar Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-accent-green rounded-lg"
        title={`Profil de ${userName}`}
      >
        <Avatar
          src={userAvatar}
          alt={userName}
          fallback={userInitials}
          size="lg"
          online={true}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-primary-card rounded-lg shadow-xl border border-gray-700 overflow-hidden z-50 animate-fadeIn">
          {/* User Info Section */}
          <div className="p-4 border-b border-gray-700 bg-gradient-to-br from-primary-dark to-primary-card">
            <div className="flex items-center gap-3">
              <Avatar
                src={userAvatar}
                alt={userName}
                fallback={userInitials}
                size="md"
                online={true}
              />
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold truncate">{userName}</p>
                {userEmail && (
                  <p className="text-text-gray text-xs truncate">{userEmail}</p>
                )}
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-2">
            {menuItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <button
                  key={index}
                  onClick={item.action}
                  className={`w-full px-4 py-2.5 flex items-center gap-3 transition-colors ${
                    item.danger
                      ? 'text-red-400 hover:bg-red-500/10'
                      : 'text-text-gray hover:bg-primary-dark hover:text-white'
                  }`}
                >
                  <Icon size={18} />
                  <span className="text-sm font-medium">{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
